import { API_TYPES, post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

interface UploadWorkBadgePdfParams {
  workerId: string;
  qualification: string;
  pdfBlob: Blob;
}

const workBadgePresignedUploadUrlRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workBadgePresignedUploadUrl),
    attributes: z.object({
      qualification: z.string(),
    }),
  }),
});

const workBadgePresignedUploadUrlResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workBadgePresignedUploadUrl),
    id: z.string().min(1),
    attributes: z.object({
      presignedUrl: z.string().min(1),
      workerId: z.string().min(1),
      fileKey: z.string().min(1),
    }),
  }),
});

export type UploadWorkBadgePdfResponse = z.infer<typeof workBadgePresignedUploadUrlResponseSchema>;
export type UploadWorkBadgePdfRequest = z.infer<typeof workBadgePresignedUploadUrlRequestSchema>;

async function fetchWorkBadgePresignedUploadUrl(workerId: string, qualification: string) {
  const response = await post({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/work-badge-presigned-upload-urls`,
    data: {
      data: {
        type: API_TYPES.workBadgePresignedUploadUrl,
        attributes: {
          qualification,
        },
      },
    },
    requestSchema: workBadgePresignedUploadUrlRequestSchema,
    responseSchema: workBadgePresignedUploadUrlResponseSchema,
  });

  return response.data;
}

export function useUploadWorkBadgePdf(
  options: UseMutationOptions<string, Error, UploadWorkBadgePdfParams> = {}
) {
  return useMutation({
    mutationFn: async ({ workerId, qualification, pdfBlob }) => {
      const { data } = await fetchWorkBadgePresignedUploadUrl(workerId, qualification);
      const { presignedUrl, fileKey } = data.attributes;

      await axios.put(presignedUrl, pdfBlob, {
        headers: { "Content-Type": "application/pdf" },
      });

      return fileKey;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.WORK_BADGE_PDF_UPLOAD_FAILURE,
    },
    ...options,
  });
}
